import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  BarChart3,
  Calendar,
  Check,
  ClipboardList,
  Clock,
  Eye,
  Film,
  Megaphone,
  RefreshCw,
  Scissors,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";
import Footer from "../Components/footer";
import creatorPage from "../images/creatorPage.png";

const NEON = "#84ff00";
const PURPLE = "#9146FF";
const PURPLE_DEEP = "#7a3ae7";
const BG = "#07060d";
const SURFACE = "rgba(255,255,255,0.04)";
const HAIRLINE = "rgba(255,255,255,0.08)";
const GRID_BG =
  "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)";

function Section({ children, className = "" }) {
  return (
    <section className={`mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 sm:py-28 ${className}`}>
      {children}
    </section>
  );
}

function CTAButton({ to, variant = "primary", children }) {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5";

  if (variant === "primary") {
    return (
      <Link
        to={to}
        className={`${base} text-black`}
        style={{ background: NEON, boxShadow: `0 10px 40px ${NEON}40` }}
      >
        {children}
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={`${base} text-white`}
      style={{
        background: `linear-gradient(135deg, ${PURPLE}, ${PURPLE_DEEP})`,
        boxShadow: `0 10px 40px ${PURPLE}55, inset 0 1px 0 rgba(255,255,255,.15)`,
      }}
    >
      {children}
    </Link>
  );
}

function TrustBullet({ children }) {
  return (
    <li className="flex items-center gap-2 text-sm text-zinc-400">
      <Check className="h-4 w-4 shrink-0" style={{ color: NEON }} />
      <span>{children}</span>
    </li>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 p-6 backdrop-blur-xl ${className}`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
}

function ScreenshotSlot() {
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border"
      style={{
        aspectRatio: "4/3",
        borderColor: HAIRLINE,
        backgroundColor: SURFACE,
        boxShadow: `0 30px 80px -40px ${NEON}40`,
      }}
    >
      <img
        src={creatorPage}
        alt="Creator profile preview"
        className="absolute inset-0 h-full w-full object-cover opacity-85"
      />
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="rounded-full border px-3 py-1 text-xs uppercase tracking-wider text-zinc-300"
          style={{ borderColor: HAIRLINE, backgroundColor: "rgba(0,0,0,0.35)" }}
        >
          Campaign early access
        </span>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <Card className="h-full transition-colors hover:border-white/15">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: "rgba(132,255,0,0.1)" }}
      >
        <Icon className="h-5 w-5" style={{ color: NEON }} />
      </div>
      <h3 className="mt-5 text-xl font-semibold text-zinc-100">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">{children}</p>
    </Card>
  );
}

function FieldMock({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-2 text-sm font-medium text-zinc-100">{value}</p>
    </div>
  );
}

function StepCard({ n, title, description, icon: Icon, accent = "neon" }) {
  const color = accent === "purple" ? PURPLE : NEON;

  return (
    <div
      className="flex flex-col rounded-xl border p-6 transition-colors hover:border-white/15"
      style={{
        backgroundColor: SURFACE,
        borderColor: `${color}33`,
        boxShadow: `0 20px 60px -40px ${color}50`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: color, color: "#0a0f0d" }}
        >
          {n}
        </div>
        <span className="text-xs font-medium uppercase tracking-wider" style={{ color }}>
          Step {n}
        </span>
      </div>
      <div
        className="mt-5 flex h-10 w-10 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-zinc-100">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{description}</p>
    </div>
  );
}

export default function LaunchClippingCampaign() {
  return (
    <>
      <SEO
        title="Launch a Twitch or Kick Clipping Campaign | Kre8"
        description="Create a Kre8 clipping campaign draft for Twitch or Kick streams and turn stream moments into TikToks, Shorts, and Reels."
        canonicalPath="/launch-clipping-campaign"
        structuredData={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Launch a Clipping Campaign", path: "/launch-clipping-campaign" },
        ])}
      />
      <TheNav />
      <main className="min-h-screen antialiased" style={{ backgroundColor: BG, color: "#e5e7eb" }}>
        <Section className="relative overflow-hidden !pt-24 sm:!pt-28">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-80"
            style={{
              background: `radial-gradient(60% 50% at 20% 20%, ${PURPLE}33, transparent 60%), radial-gradient(50% 50% at 85% 30%, ${NEON}1a, transparent 60%), radial-gradient(40% 40% at 50% 90%, ${PURPLE_DEEP}33, transparent 60%)`,
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-[0.06]"
            style={{
              backgroundImage: GRID_BG,
              backgroundSize: "60px 60px",
              maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            }}
          />
          <div className="relative mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="text-center lg:text-left">
              <p
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium"
                style={{
                  borderColor: `${NEON}55`,
                  color: NEON,
                  background: `${NEON}10`,
                  boxShadow: `0 0 24px ${NEON}22`,
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Streamer campaign access
              </p>
              <h1 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl leading-[1.05]">
                Launch a clipping campaign for your Twitch or Kick streams
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-zinc-400 sm:text-xl lg:mx-0">
                Set an RPM, submission window, tracking cap, and platform payout rule so clippers know exactly how views turn into payouts.
              </p>
              <ul className="mx-auto mt-8 flex max-w-xl flex-wrap justify-center gap-x-6 gap-y-3 lg:justify-start">
                <TrustBullet>No admin access required</TrustBullet>
                <TrustBullet>You choose the price per 1,000 views</TrustBullet>
                <TrustBullet>You set how long clippers have to submit links</TrustBullet>
                <TrustBullet>You choose how duplicate platform views are paid</TrustBullet>
              </ul>
              <div className="mt-10 flex flex-wrap justify-center gap-3 lg:justify-start">
                <CTAButton to="/signup?role=creator&focus=clipping-campaigns&next=%2Fclipping-campaigns%2Fnew">Create Campaign Draft</CTAButton>
                <CTAButton to="/for-clippers" variant="secondary">
                  Send Clippers Here
                </CTAButton>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
              <ScreenshotSlot />
            </div>
          </div>
        </Section>

        <Section className="!pt-0">
          <div className="grid gap-5 md:grid-cols-3">
            <InfoCard icon={Megaphone} title="Built for discovery">
              Campaigns help you create more chances for one great stream moment to reach new viewers.
            </InfoCard>
            <InfoCard icon={Users} title="More than one clipper">
              Instead of asking one person to find everything, campaigns let multiple clippers take a shot at your best moments.
            </InfoCard>
            <InfoCard icon={TrendingUp} title="Growth over busywork">
              The point is not just more clips. The point is more short form content that can pull viewers back to your stream.
            </InfoCard>
          </div>
        </Section>

        <Section>
          <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr]">
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>
                    Campaign setup preview
                  </p>
                  <h2 className="mt-3 text-3xl font-bold text-white">View based payout controls</h2>
                </div>
                <BadgeDollarSign className="h-8 w-8" style={{ color: NEON }} />
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <FieldMock label="Campaign budget" value="$500 beta budget" icon={Wallet} />
                <FieldMock label="RPM" value="$2.50 per 1,000 views" icon={BarChart3} />
                <FieldMock label="Link submission window" value="48 hours after posting" icon={Clock} />
                <FieldMock label="Tracking cap" value="30 days after clip is posted" icon={Calendar} />
                <FieldMock label="Initial check" value="24 to 48 hours and 7 day mark" icon={Eye} />
                <FieldMock label="Platform rule" value="Pay each platform or only the top viewed post" icon={RefreshCw} />
              </div>
            </Card>
            <Card>
              <h2 className="text-2xl font-bold text-white">Two platform payout choices</h2>
              <div className="mt-5 space-y-4">
                <div className="rounded-xl border border-emerald-300/20 bg-emerald-500/10 p-4">
                  <p className="font-semibold text-emerald-100">Pay each platform</p>
                  <p className="mt-2 text-sm text-emerald-100/70">
                    If the same clip is posted to TikTok, Shorts, and Reels, each platform can earn based on its own views.
                  </p>
                </div>
                <div className="rounded-xl border border-purple-300/20 bg-purple-500/10 p-4">
                  <p className="font-semibold text-purple-100">Pay the top platform only</p>
                  <p className="mt-2 text-sm text-purple-100/70">
                    If the same clip is posted in multiple places, only the version with the most views is paid.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </Section>

        <Section>
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: NEON }}>
              Campaign basics
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Give clippers a clear reason to create
            </h2>
            <p className="mt-4 text-zinc-400">
              A strong campaign starts with clear stream links, examples, style notes, and a goal for the content.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <StepCard
              n="1"
              title="Share the content"
              description="Point clippers toward the streams, VODs, timestamps, or moments that matter most."
              icon={Film}
            />
            <StepCard
              n="2"
              title="Set the direction"
              description="Tell them the platform, length, caption style, pacing, RPM, and how views will be tracked."
              icon={ClipboardList}
              accent="purple"
            />
            <StepCard
              n="3"
              title="Grow from the best clips"
              description="Use 24 to 48 hour growth, 7 day checks, and the 30 day cap to decide what paid views count."
              icon={Scissors}
            />
          </div>
        </Section>

        <Section>
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 p-10 text-center sm:p-16"
            style={{
              background: `linear-gradient(135deg, ${PURPLE}55, #0a0a14 50%, ${NEON}33)`,
              boxShadow: `0 30px 100px rgba(0,0,0,0.6), 0 0 100px ${PURPLE}33`,
            }}
          >
            <Scissors className="mx-auto h-10 w-10" style={{ color: NEON }} />
            <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Prepare your first clipping campaign
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-zinc-300">
              Create a creator account, build your campaign draft, and request beta review when the brief is ready.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <CTAButton to="/signup?role=creator&focus=clipping-campaigns&next=%2Fclipping-campaigns%2Fnew">Create Campaign Draft</CTAButton>
              <CTAButton to="/clipping-campaigns/new" variant="secondary">Open Campaign Builder</CTAButton>
            </div>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
